import { ReactNode, useCallback, useMemo, useRef } from 'react';
import { ErrorTypes, FieldTypes, Filter } from '@fabric-ui/components';
import { X } from '@fabric-ui/icons';
import { emptyArray, emptyObject, iconSize } from '@fabric-ui/utils';
import { IconButton } from '@mui/material';
import { useSnackbar } from 'notistack';
import { buildAliasResolution, getCascadeOperands, rearrangeObject } from '../utils/utils';
import { clickthroughPrefix } from '../views/CustomDashboards/components/Clickthrough/consts';
import { AliasResolution } from '../views/CustomDashboards/types';
import { typeLabelMap } from '../views/Sources/Mapping/types';
import { filtersFieldToIgnore, getFieldLabel } from '../views/Tickets/ticket.types';
import { getOptionsByField } from './AvFilters.utils';
import type { AvFiltersProps } from './AvFiltersBase';
import { FieldFilterBase } from './FieldFilterBase';
import { useFilterConfig, useFilterContext, useFilterFeatures } from './filters/FilterContext';
import { FilterOption } from './filters/types';
import useFilterFields, { getVisibleDims } from './useFilterFields';

const getSelectedFiltersMap = (selectedFilters: string[]) => {
  const acc: Record<string, boolean> = {};
  selectedFilters.forEach(filter => {
    acc[filter] = true;
  });
  return acc;
};

const flatArrayOfOptionsWithCustomTitle = (
  data: FilterOption[],
  _label: ReactNode,
  titleGenerator?: (value: unknown) => ReactNode
): FilterOption[] =>
  [
    ...new Set(
      data
        .filter((item): item is FilterOption => item != null)
        .map(({ value }) => value)
        .flat(1) as string[]
    ),
  ].map(value => ({
    title: (titleGenerator?.(value) || value) as string,
    value,
  }));

type UseAvFiltersStateProps = Pick<
  AvFiltersProps,
  | 'filters'
  | 'staticFilters'
  | 'updateFilters'
  | 'setFilters'
  | 'isActiveFilter'
  | 'dims'
  | 'metrics'
  | 'calcDims'
  | 'usage'
  | 'overrideExtra'
  | 'displayNameMapFormatter'
  | 'isFilterExpression'
  | 'removeFieldButton'
  | 'queryKey'
  | 'isGlobalFilter'
  | 'ignoreVisibilityConfig'
  | 'extraOperands'
  | 'cascadeFilters'
>;

/**
 * Encapsulates the derived filter-selection state for AvFiltersBase: which fields are
 * currently selected as filters, the merged field metadata lookup, and the handlers for
 * adding/removing/reordering selected filters and rendering a filter chip.
 */
export const useAvFiltersState = ({
  filters,
  staticFilters = emptyArray,
  updateFilters,
  setFilters,
  isActiveFilter,
  dims = emptyArray,
  metrics = emptyArray,
  calcDims = emptyArray,
  usage,
  overrideExtra,
  displayNameMapFormatter = emptyObject,
  isFilterExpression = false,
  removeFieldButton,
  queryKey,
  isGlobalFilter = false,
  ignoreVisibilityConfig = false,
  extraOperands,
  cascadeFilters,
}: UseAvFiltersStateProps) => {
  const { disabled, activeProjName } = useFilterConfig();
  const { accountEntities, measurements } = useFilterContext();
  const { fieldMap = {}, aggProjs = {} } = accountEntities ?? {};
  const { enqueueSnackbar } = useSnackbar();
  const { isPopulatedFields, isRecommendedFieldsEnabled, isTopFields, showErrorToast } = useFilterFeatures();
  const filtersRefsMap = useRef({});

  const visibleDims = useMemo(
    () => getVisibleDims({ dims, dimsToRemove: new Set(staticFilters), isUnpopulatedFieldsActive: true, ignoreVisibilityConfig }),
    [dims, staticFilters, ignoreVisibilityConfig]
  );

  const fieldMeasurementsMap = useMemo(
    () => ({
      ...fieldMap,
      ...measurements.visible.reduce(
        (acc, { systemName, displayName, type }) => ({
          ...acc,
          [systemName]: {
            value: systemName,
            pathAlias: systemName,
            title: displayName,
            group: FieldTypes.Measurement,
            entityTypeId: { builtin: true },
            type,
          },
        }),
        {}
      ),
      ...calcDims.reduce(
        (acc, { value, title, pathAlias, type, group, entityTypeId }) => ({
          ...acc,
          [value]: {
            value,
            pathAlias,
            title,
            group,
            entityTypeId,
            type,
          },
        }),
        {}
      ),
    }),
    [measurements.visible, fieldMap, calcDims]
  );

  const allSelectedFilters = useMemo(() => {
    const setupErrorFields: string[] = [];
    const allFieldsList = new Set([...visibleDims, ...calcDims, ...metrics].map(({ value }) => value));
    const hiddenFieldsList = ignoreVisibilityConfig
      ? new Set<string>()
      : new Set(dims.filter(d => d.visibilityConfig?.config?.hidden).map(({ value }) => value));
    const filteredFilters = [
      ...new Set(
        Object.keys(filters).filter(originalField => {
          const field = originalField.replace('amp;', '');
          const isIncludedInAllFieldsList = !isGlobalFilter || allFieldsList.has(field);
          const canIgnoreFilter = filtersFieldToIgnore.includes(field) || field.includes(clickthroughPrefix);
          const isStaticFilter = staticFilters.includes(field);
          const isHiddenField = hiddenFieldsList.has(field);

          if (!isIncludedInAllFieldsList && !canIgnoreFilter && !isStaticFilter && !isHiddenField) {
            setupErrorFields.push(field);
          }

          return (
            !isStaticFilter && !canIgnoreFilter && !isHiddenField && !!fieldMeasurementsMap[originalField] && isIncludedInAllFieldsList
          );
        })
      ),
    ];
    if (setupErrorFields.length) {
      const error = `${ErrorTypes.Setup}: unknown columns: ${setupErrorFields.join(', ')}`;
      console.warn(error);
      if (showErrorToast) {
        enqueueSnackbar(error, { variant: 'error' });
      }
    }
    return filteredFilters;
  }, [
    filters,
    visibleDims,
    metrics,
    calcDims,
    dims,
    ignoreVisibilityConfig,
    fieldMeasurementsMap,
    isGlobalFilter,
    staticFilters,
    showErrorToast,
    enqueueSnackbar,
  ]);

  const selectedFieldsIncludingStatic = useMemo(
    () => [...allSelectedFilters, ...staticFilters.filter(field => field in filters)],
    [allSelectedFilters, staticFilters, filters]
  );

  const allSelectedFiltersSet = useMemo(() => {
    const acc: Record<string, { value: string }> = {};
    selectedFieldsIncludingStatic.forEach(selectedFilter => {
      acc[selectedFilter] = { value: selectedFilter };
    });
    return acc;
  }, [selectedFieldsIncludingStatic]);

  const { fields, initFilters, resetFilters, ...filtersProps } = useFilterFields({
    dims,
    metrics,
    calcDims,
    topFieldsEnabled: isTopFields,
    populatedFieldsEnabled: isPopulatedFields,
    selectedFields: allSelectedFiltersSet,
    ignoreVisibilityConfig,
  });

  const selectedFiltersByFilterType = useMemo(() => {
    const filteredFieldsList = new Set(fields.map(({ value }) => value));

    return selectedFieldsIncludingStatic.filter(field => filteredFieldsList.has(field));
  }, [selectedFieldsIncludingStatic, fields]);

  const allSelectedFiltersMap = useMemo(() => getSelectedFiltersMap(allSelectedFilters), [allSelectedFilters]);

  const activeFilter = allSelectedFilters.some(field => filters[field]?.length) || isActiveFilter;

  const removeField = useCallback(
    (field: string) => {
      const { [field]: deleted, ...rest } = filters;
      setFilters(rest);
    },
    [filters, setFilters]
  );

  const onSelectedFiltersSelect = useCallback(
    val => {
      const newFilter = val.find(f => !selectedFiltersByFilterType.includes(f));
      const deletedFilter = selectedFiltersByFilterType.find(f => !val.includes(f));
      if (newFilter) {
        updateFilters(newFilter, []);
        setTimeout(() => filtersRefsMap.current[newFilter]?.querySelector('button')?.click?.());
      } else if (deletedFilter) {
        removeField(deletedFilter);
      }
    },
    [selectedFiltersByFilterType, updateFilters, removeField]
  );

  const handleReorder = useCallback((newOrder: string[]) => setFilters(rearrangeObject(filters, newOrder)), [filters, setFilters]);

  /**
   * Cascading: each field's option list is narrowed by the *other* fields' filters - never by its own, which
   * would hide every value the user hasn't already picked. Resolved against `dims` rather than `visibleDims`
   * so the aliases match the ones the widget queries use.
   */
  const cascadeByField = useMemo(() => {
    const operands = cascadeFilters?.and?.operands;
    if (!operands?.length) {
      return emptyObject as Record<string, { contextFilter?: Filter; aliasResolution?: AliasResolution }>;
    }
    return allSelectedFilters.reduce<Record<string, { contextFilter?: Filter; aliasResolution?: AliasResolution }>>((acc, field) => {
      const siblingOperands = getCascadeOperands({ cascadeFilters, field });
      const contextFilter = siblingOperands.length ? { and: { operands: siblingOperands } } : undefined;
      acc[field] = { contextFilter, aliasResolution: contextFilter && buildAliasResolution({ dims, contextFilter }) };
      return acc;
    }, {});
  }, [cascadeFilters, allSelectedFilters, dims]);

  const renderFilterChip = useCallback(
    (field: string) => {
      const fieldActiveProjName = isGlobalFilter ? visibleDims.find(f => f.value === field)?.mainProjId?.name : activeProjName;

      return (
        <FieldFilterBase
          setRef={ref => (filtersRefsMap.current[field] = ref)}
          filters={filters}
          getOptions={displayNameMapFormatter[field] ? flatArrayOfOptionsWithCustomTitle : getOptionsByField[field]}
          onChange={updateFilters}
          field={field}
          isEnabled={allSelectedFiltersMap[field]}
          overrideExtra={overrideExtra}
          extraOperands={extraOperands}
          label={getFieldLabel({
            field,
            fieldMap: fieldMeasurementsMap,
            projectionName: activeProjName,
            aggProjs,
          })}
          fieldType={typeLabelMap[fieldMeasurementsMap[field]?.type]}
          activeProjName={fieldActiveProjName}
          queryKey={queryKey}
          getValue={displayNameMapFormatter[field]}
          isFilterExpression={isFilterExpression}
          usage={usage}
          isGlobalFilter={isGlobalFilter}
          optionsContextFilter={cascadeByField[field]?.contextFilter}
          aliasResolution={cascadeByField[field]?.aliasResolution}
          selectIcon={
            removeFieldButton ? (
              disabled ? (
                <div />
              ) : (
                <IconButton
                  sx={iconSize(16)}
                  size="small"
                  onClick={e => {
                    e.stopPropagation();
                    removeField(field);
                  }}>
                  <X />
                </IconButton>
              )
            ) : undefined
          }
        />
      );
    },
    [
      isGlobalFilter,
      visibleDims,
      activeProjName,
      filters,
      displayNameMapFormatter,
      updateFilters,
      allSelectedFiltersMap,
      overrideExtra,
      extraOperands,
      fieldMeasurementsMap,
      aggProjs,
      queryKey,
      isFilterExpression,
      usage,
      removeFieldButton,
      disabled,
      removeField,
      cascadeByField,
    ]
  );

  return {
    visibleDims,
    fieldMeasurementsMap,
    allSelectedFilters,
    selectedFiltersByFilterType,
    activeFilter,
    fields,
    filtersProps,
    initFilters,
    resetFilters,
    removeField,
    onSelectedFiltersSelect,
    handleReorder,
    renderFilterChip,
    measurements,
    isRecommendedFieldsEnabled,
    isTopFields,
  };
};
